import { supabase } from '@/integrations/supabase/client'
import { AUTH_COOKIE_NAME, syncAccessTokenCookie } from '@/lib/crossDomainAuth'

const SUPABASE_AUTH_STORAGE_PREFIX = 'sb-'
const DANGEROUS_SESSION_PARAMS = [
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'session_id',
]

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=')
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

export function getSessionIdFromAccessToken(accessToken: string | null | undefined): string | null {
  if (!accessToken) return null
  const payload = decodeJwtPayload(accessToken)
  const sessionId = payload?.session_id
  return typeof sessionId === 'string' && sessionId.length > 0 ? sessionId : null
}

export async function getCurrentAuthSessionId(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession()
  return getSessionIdFromAccessToken(session?.access_token)
}

export async function clearClientAuthStateForLogin(): Promise<void> {
  await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined)
  syncAccessTokenCookie(null)

  if (typeof window === 'undefined') return

  for (const storage of [window.localStorage, window.sessionStorage]) {
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i)
      if (key?.startsWith(SUPABASE_AUTH_STORAGE_PREFIX) || key === AUTH_COOKIE_NAME) {
        storage.removeItem(key)
      }
    }
  }
}

export function stripExternalSessionParamsFromUrl(): void {
  if (typeof window === 'undefined') return

  const url = new URL(window.location.href)
  let changed = false

  for (const key of DANGEROUS_SESSION_PARAMS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key)
      changed = true
    }
  }

  if (url.hash) {
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
    for (const key of DANGEROUS_SESSION_PARAMS) {
      if (hashParams.has(key)) {
        hashParams.delete(key)
        changed = true
      }
    }
    const newHash = hashParams.toString()
    url.hash = newHash ? `#${newHash}` : ''
  }

  if (changed) {
    window.history.replaceState(window.history.state, document.title, url.toString())
  }
}
